/**
 * Polling badge layout yang ramah sinyal lemah, Okt 2026 (Fase 3).
 *
 * Dipakai oleh layout Nakes, Gudang, Finance, dan Marketing menggantikan
 * setInterval biasa. Perbedaan utama:
 *  - Putaran berikutnya baru dijadwalkan SETELAH putaran sekarang selesai,
 *    jadi request tidak pernah menumpuk di sinyal lemah.
 *  - Setiap request polling punya timeout (POLL_REQUEST_TIMEOUT_MS).
 *  - Satu putaran dibatasi waktu total (ROUND_DEADLINE_MS) supaya rantai
 *    polling tidak pernah berhenti selamanya.
 *  - Kalau putaran gagal total, jedanya melebar (x2, x4, maksimal x4).
 *  - Berhenti saat tab tersembunyi atau perangkat offline, lanjut satu kali
 *    segera setelah kembali.
 */

import axios from 'axios';

export const POLL_INTERVAL_MS = 30000;
// Timeout axios mencakup seluruh unduhan respons. Dibuat longgar (25 detik)
// supaya daftar besar (supply-requests, logistic-returns, chat) di sinyal
// lemah tetap sempat selesai, tapi tidak menggantung tanpa batas.
export const POLL_REQUEST_TIMEOUT_MS = 25000;
// Maksimal request polling yang berjalan bersamaan, supaya slot koneksi
// browser tidak habis dan klik user tidak antre di belakang polling.
const POLL_CONCURRENCY = 3;
const ROUND_DEADLINE_MS = 75000;
const MAX_BACKOFF_FACTOR = 4;
// Jika tab kembali terlihat dan putaran terakhir selesai kurang dari ini,
// tidak perlu langsung fetch lagi.
const MIN_RESUME_GAP_MS = 5000;

/**
 * GET dengan timeout polling. Config lain (headers, params) diteruskan.
 */
export function pollGet(url, config = {}) {
  return axios.get(url, { timeout: POLL_REQUEST_TIMEOUT_MS, ...config });
}

/**
 * Jalankan beberapa tugas fetch bersamaan (maksimal `concurrency` sekaligus). Setiap tugas adalah fungsi async
 * yang melempar error kalau gagal. Mengembalikan true jika minimal satu
 * tugas berhasil (atau tidak ada tugas), false jika semuanya gagal.
 * Error tidak pernah dilempar keluar, sama seperti perilaku lama (silent).
 */
export async function runPollTasks(tasks, concurrency = POLL_CONCURRENCY) {
  if (!tasks || tasks.length === 0) return true;
  const results = new Array(tasks.length).fill(false);
  let next = 0;
  const worker = async () => {
    while (next < tasks.length) {
      const i = next++;
      try {
        await tasks[i]();
        results[i] = true;
      } catch (_e) {
        results[i] = false;
      }
    }
  };
  const workers = [];
  for (let w = 0; w < Math.min(concurrency, tasks.length); w++) workers.push(worker());
  await Promise.all(workers);
  return results.some(Boolean);
}

/**
 * Mulai polling. `run` adalah fungsi async yang mengembalikan false kalau
 * putaran gagal total (memicu backoff), selain itu dianggap sukses.
 * Mengembalikan fungsi stop() untuk dipanggil saat cleanup.
 */
export function startPolling(run, { intervalMs = POLL_INTERVAL_MS, deadlineMs = ROUND_DEADLINE_MS, minResumeGapMs = MIN_RESUME_GAP_MS } = {}) {
  let timer = null;
  let stopped = false;
  let running = false;
  let failureStreak = 0;
  let lastFinishedAt = 0;

  const clearTimer = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const nextDelay = () => {
    const factor = Math.min(2 ** failureStreak, MAX_BACKOFF_FACTOR);
    return intervalMs * (failureStreak > 0 ? factor : 1);
  };

  const schedule = () => {
    clearTimer();
    if (stopped || document.hidden) return;
    timer = setTimeout(tick, nextDelay());
  };

  async function tick() {
    timer = null;
    if (stopped || running) return;
    if (document.hidden) return;
    // Offline: lewati putaran ini tanpa menambah backoff, tunggu event online.
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      schedule();
      return;
    }
    running = true;
    let ok = true;
    let deadlineTimer;
    try {
      const deadline = new Promise((resolve) => {
        deadlineTimer = setTimeout(() => resolve(false), deadlineMs);
      });
      const result = await Promise.race([Promise.resolve().then(run), deadline]);
      ok = result !== false;
    } catch (_e) {
      ok = false;
    } finally {
      clearTimeout(deadlineTimer);
      running = false;
      lastFinishedAt = Date.now();
    }
    failureStreak = ok ? 0 : failureStreak + 1;
    schedule();
  }

  const resumeNow = () => {
    if (stopped || running || document.hidden) return;
    if (Date.now() - lastFinishedAt < minResumeGapMs) {
      schedule();
      return;
    }
    clearTimer();
    tick();
  };

  const onVisibility = () => {
    if (document.hidden) {
      clearTimer();
    } else {
      resumeNow();
    }
  };

  const onOnline = () => {
    // Jaringan kembali: reset backoff dan fetch segera.
    failureStreak = 0;
    if (stopped || running || document.hidden) return;
    clearTimer();
    tick();
  };

  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('online', onOnline);

  // Fetch pertama segera, sama seperti perilaku lama.
  tick();

  return function stop() {
    stopped = true;
    clearTimer();
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('online', onOnline);
  };
}
